import { useState } from 'react';

import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';

import { testPosifloraConnection } from '../../api/posifloraApi';
import {
  useDisconnectPosiflora,
  usePosifloraSettings,
  useUpdatePosifloraCredentials,
  useUpdatePosifloraSettings,
} from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  MaskedCredential,
  PosifloraCapabilities,
  UpdatePosifloraSettingsRequest,
} from '../../model/settingsTypes';
import {
  ConfirmSyncDialog,
  FeedbackSnackbar,
  FieldRow,
  MaskedCredentialField,
  MultiToggleField,
  SecondaryFormActions,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';
import { POSIFLORA_CAPABILITY_OPTIONS } from '../options';

function formatSyncTimestamp(value: string | null, neverLabel: string): string {
  if (!value) {
    return neverLabel;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return neverLabel;
  }
  return new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(
    date,
  );
}

/** The enabled subset of `PosifloraCapabilities`, restricted to the 7 MVP-exposed toggle keys. */
function enabledCapabilityKeys(capabilities: PosifloraCapabilities | null): string[] {
  if (!capabilities) {
    return [];
  }
  return POSIFLORA_CAPABILITY_OPTIONS.map((option) => option.value).filter(
    (key) => capabilities[key],
  );
}

/**
 * Rebuilds the full 11-key `PosifloraCapabilities` payload from the 7 toggled keys, passing the 4
 * future-scoped keys (`customerExport`/`customerImport`/`loyalty`/`purchases` — no MVP toggle)
 * through unchanged from whatever the server last returned, so a save never silently stomps a
 * value this screen doesn't show.
 */
function mergeCapabilities(
  current: PosifloraCapabilities | null,
  enabledKeys: string[],
): PosifloraCapabilities {
  const enabled = new Set(enabledKeys);
  return {
    catalog: enabled.has('catalog'),
    inventory: enabled.has('inventory'),
    price: enabled.has('price'),
    store: enabled.has('store'),
    orderExport: enabled.has('orderExport'),
    payment: enabled.has('payment'),
    returns: enabled.has('returns'),
    customerExport: current?.customerExport ?? false,
    customerImport: current?.customerImport ?? false,
    loyalty: current?.loyalty ?? false,
    purchases: current?.purchases ?? false,
  };
}

/**
 * Posiflora ERP connection settings — MVP scope is exactly one ERP type, but the shape (a
 * `configured` first-run flag, a separate credentials sub-resource, a full disconnect) is written
 * so a second ERP vendor can be added later as a sibling resource, not a rewrite of this one.
 */
export function ErpPosifloraSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('erp-posiflora');
  const query = usePosifloraSettings();
  const update = useUpdatePosifloraSettings();
  const updateCredentials = useUpdatePosifloraCredentials();
  const disconnect = useDisconnectPosiflora();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const [credentialUsername, setCredentialUsername] = useState('');
  const [credentialPassword, setCredentialPassword] = useState('');
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const resetCredentialsDraft = () => {
    setCredentialUsername('');
    setCredentialPassword('');
  };

  const handleSave = () => {
    const draft = form.draft;
    if (!draft) {
      return;
    }
    const body: UpdatePosifloraSettingsRequest = {
      baseUrl: draft.baseUrl ?? '',
      // The operator may click Save without ever touching the enabled/disabled switch on a
      // brand-new connection (`draft.status` starts `null`) — default conservatively to disabled
      // rather than silently turning sync on for a connection nobody has confirmed yet.
      status: draft.status ?? 'DISABLED',
      capabilities: draft.capabilities ?? mergeCapabilities(null, []),
    };
    update.mutate(body, {
      onSuccess: () => success(t('erpSettings.savedFeedback')),
      onError: (error) => notifyError(error.message),
    });
  };

  const credentialsDraftDirty = credentialUsername.trim() !== '' || credentialPassword.trim() !== '';

  const handleSaveCredentials = () => {
    if (credentialUsername.trim() === '') {
      // The username field can be left blank while only the password is being rotated (a
      // plausible, even likely, interaction given the field's own helper text prompts re-entry
      // "whenever you change the password") — without this, the Save button looked enabled and
      // the click silently did nothing, leaving the operator unable to tell whether a password
      // rotation actually took effect.
      notifyError(t('erpSettings.connectionSection.usernameRequiredError'));
      return;
    }
    updateCredentials.mutate(
      {
        username: credentialUsername.trim(),
        ...(credentialPassword.trim() !== '' ? { password: credentialPassword } : {}),
      },
      {
        onSuccess: () => {
          resetCredentialsDraft();
          success(t('erpSettings.credentialsSavedFeedback'));
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  const handleDisconnect = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => {
        setDisconnectOpen(false);
        resetCredentialsDraft();
        success(t('erpSettings.disconnectedFeedback'));
      },
      onError: (error) => {
        setDisconnectOpen(false);
        notifyError(error.message);
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('erpSettings.title')}
      subtitle={t('erpSettings.subtitle')}
      icon={<CloudSyncRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {(data) => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        const credentialsEnabled = data.configured && canManageCredentials;
        const passwordCredential: MaskedCredential | null = data.credentialsConfigured
          ? {
              kind: 'PASSWORD',
              masked: t('erpSettings.connectionSection.credentialsConfiguredMasked'),
              present: true,
              version: 0,
              updatedAt: data.updatedAt,
            }
          : null;

        return (
          <Stack spacing={2.5}>
            <SettingsCard
              title={t('erpSettings.connectionSection.title')}
              description={t('erpSettings.connectionSection.description')}
              icon={<HubRoundedIcon />}
            >
              <TextInputField
                label={t('erpSettings.connectionSection.baseUrlLabel')}
                description={t('erpSettings.connectionSection.baseUrlDescription')}
                type="url"
                value={draft.baseUrl ?? ''}
                onChange={(value) => form.setField('baseUrl', value)}
                disabled={disabled}
                placeholder="https://api.posiflora.com"
              />
              <SwitchField
                label={t('erpSettings.connectionSection.statusLabel')}
                description={t('erpSettings.connectionSection.statusDescription')}
                checked={draft.status === 'ENABLED'}
                onChange={(checked) => form.setField('status', checked ? 'ENABLED' : 'DISABLED')}
                disabled={disabled}
              />
              <FieldRow
                label={t('erpSettings.connectionSection.credentialStatusLabel')}
                description={t('erpSettings.connectionSection.credentialStatusDescription')}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color={data.credentialsConfigured ? 'success' : 'default'}
                  label={
                    data.credentialsConfigured
                      ? t('erpSettings.connectionSection.connectedChip')
                      : t('erpSettings.connectionSection.notConnectedChip')
                  }
                />
              </FieldRow>
              <FieldRow
                label={t('erpSettings.connectionSection.lastSyncLabel')}
                description={t('erpSettings.connectionSection.lastSyncDescription')}
              >
                <Typography variant="body2">
                  {formatSyncTimestamp(data.lastSyncAt, t('erpSettings.connectionSection.neverSynced'))}
                </Typography>
              </FieldRow>

              {canManageCredentials && !data.configured ? (
                <Alert severity="info">
                  {t('erpSettings.connectionSection.credentialsUnavailableNotice')}
                </Alert>
              ) : null}
              <TextInputField
                label={t('erpSettings.connectionSection.usernameLabel')}
                description={t('erpSettings.connectionSection.usernameDescription')}
                value={credentialUsername}
                onChange={setCredentialUsername}
                disabled={!credentialsEnabled}
                placeholder={t('erpSettings.connectionSection.usernamePlaceholder')}
              />
              <MaskedCredentialField
                label={t('erpSettings.connectionSection.passwordLabel')}
                description={
                  data.configured
                    ? t('erpSettings.connectionSection.passwordDescription')
                    : t('erpSettings.connectionSection.passwordUnavailableDescription')
                }
                credential={passwordCredential}
                secret={credentialPassword}
                onSecretChange={setCredentialPassword}
                disabled={!credentialsEnabled}
              />
              {canManageCredentials ? (
                <SecondaryFormActions
                  dirty={credentialsDraftDirty}
                  saving={updateCredentials.isPending}
                  dirtyLabel={t('erpSettings.connectionSection.credentialsUnsavedLabel')}
                  cleanLabel={t('erpSettings.connectionSection.credentialsSavedLabel')}
                  saveLabel={t('erpSettings.connectionSection.saveCredentialsLabel')}
                  onSave={handleSaveCredentials}
                  onReset={resetCredentialsDraft}
                />
              ) : null}

              <FieldRow
                label={t('erpSettings.connectionSection.testConnectionLabel')}
                description={
                  data.configured
                    ? t('erpSettings.connectionSection.testConnectionDescription')
                    : t('erpSettings.connectionSection.testConnectionUnavailableDescription')
                }
                alignTop
              >
                <TestConnectionButton
                  run={testPosifloraConnection}
                  disabled={!canManage || !data.configured}
                />
              </FieldRow>

              <FieldRow
                label={t('erpSettings.connectionSection.disconnectLabel')}
                description={t('erpSettings.connectionSection.disconnectDescription')}
                alignTop
              >
                {data.configured ? (
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    disabled={disabled}
                    onClick={() => setDisconnectOpen(true)}
                  >
                    {t('erpSettings.connectionSection.disconnectButton')}
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('erpSettings.connectionSection.disconnectUnavailable')}
                  </Typography>
                )}
              </FieldRow>
            </SettingsCard>

            <SettingsCard
              title={t('erpSettings.capabilitiesSection.title')}
              description={t('erpSettings.capabilitiesSection.description')}
              icon={<SyncRoundedIcon />}
            >
              <MultiToggleField
                label={t('erpSettings.capabilitiesSection.enabledLabel')}
                description={t('erpSettings.capabilitiesSection.enabledDescription')}
                value={enabledCapabilityKeys(draft.capabilities)}
                options={POSIFLORA_CAPABILITY_OPTIONS.map((option) => ({
                  ...option,
                  label: t(option.label),
                }))}
                onChange={(value) =>
                  form.setField('capabilities', mergeCapabilities(draft.capabilities, value))
                }
                disabled={disabled}
              />
            </SettingsCard>

            <ConfirmSyncDialog
              open={disconnectOpen}
              title={t('erpSettings.disconnectDialog.title')}
              body={t('erpSettings.disconnectDialog.body')}
              warning={t('erpSettings.disconnectDialog.scopeWarning')}
              destructive
              ackLabel={t('erpSettings.disconnectDialog.ackLabel')}
              confirmLabel={t('erpSettings.disconnectDialog.confirmLabel')}
              onConfirm={handleDisconnect}
              onCancel={() => setDisconnectOpen(false)}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
